import { isValidElement, type ReactNode } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import type { TooltipProps } from "recharts";

/**
 * Recharts default tooltip uses a light panel; Typography also applies theme `text.primary` (light in dark mode),
 * which can win over `sx` and yield light-on-white. Use a Box surface + `color="inherit"` from `getContrastText`.
 */
export default function RechartsChartTooltip(props: TooltipProps<number, string>) {
  const theme = useTheme();
  const { active, payload, label, formatter } = props;
  const bg =
    theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.background.paper;
  const textColor = theme.palette.getContrastText(bg);

  if (!active || !payload?.length) return null;

  const showLabel = label != null && label !== "" && !(typeof label === "number" && Number.isNaN(label));

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        bgcolor: bg,
        color: textColor,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        boxShadow: theme.shadows[8],
      }}
    >
      {showLabel && (
        <Typography component="div" variant="body2" fontWeight={600} color="inherit">
          {isValidElement(label) ? label : String(label as string | number)}
        </Typography>
      )}
      {payload.map((entry, i) => {
        let valueNode: ReactNode = entry.value;
        let nameNode: ReactNode = entry.name;
        if (formatter && entry.value != null && entry.name != null) {
          const out = formatter(entry.value, entry.name, entry, i, payload);
          if (Array.isArray(out)) {
            valueNode = out[0];
            nameNode = out[1];
          } else {
            valueNode = out;
          }
        }
        return (
          <Typography key={i} variant="body2" color="inherit" sx={{ mt: showLabel || i > 0 ? 0.5 : 0 }}>
            {nameNode != null && nameNode !== "" ? (
              <>
                {nameNode}
                {" : "}
              </>
            ) : null}
            {valueNode}
          </Typography>
        );
      })}
    </Box>
  );
}
